import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface Checklist {
  id: string;
  customerName: string;
  weddingDate: string;
  makeupDate: string;
  items: {
    category: string;
    tasks: {
      id: string;
      task: string;
      completed: boolean;
    }[];
  }[];
  progress: number;
}

export default function MakeupChecklist() {
  const [checklists, setChecklists] = useState<Checklist[]>([
    {
      id: '1',
      customerName: '김미영',
      weddingDate: '2024-03-25',
      makeupDate: '2024-03-25',
      progress: 75,
      items: [
        {
          category: '의상 확인',
          tasks: [
            { id: '1-1', task: '드레스 픽업 여부 확인', completed: true },
            { id: '1-2', task: '드레스 색상 및 스타일 확인', completed: true },
            { id: '1-3', task: '액세서리 준비 확인', completed: true }
          ]
        },
        {
          category: '헤어 장신구',
          tasks: [
            { id: '2-1', task: '티아라/화관 준비', completed: true },
            { id: '2-2', task: '헤어핀/밴드 준비', completed: false },
            { id: '2-3', task: '베일 준비', completed: true }
          ]
        },
        {
          category: '메이크업 준비',
          tasks: [
            { id: '3-1', task: '피부톤 맞춤 파운데이션 준비', completed: true },
            { id: '3-2', task: '립 컬러 준비', completed: true },
            { id: '3-3', task: '속눈썹 준비', completed: false }
          ]
        },
        {
          category: '고객 확인',
          tasks: [
            { id: '4-1', task: '고객 피부 상태 확인', completed: true },
            { id: '4-2', task: '알레르기 재확인', completed: true },
            { id: '4-3', task: '선호 스타일 최종 확인', completed: false }
          ]
        },
        {
          category: '일정 확인',
          tasks: [
            { id: '5-1', task: '촬영 일정 확인', completed: true },
            { id: '5-2', task: '웨딩홀 도착 시간 확인', completed: true },
            { id: '5-3', task: '혼주 메이크업 시간 확인', completed: true }
          ]
        }
      ]
    },
    {
      id: '2',
      customerName: '이지은',
      weddingDate: '2024-04-10',
      makeupDate: '2024-04-10',
      progress: 40,
      items: [
        {
          category: '의상 확인',
          tasks: [
            { id: '6-1', task: '드레스 픽업 여부 확인', completed: true },
            { id: '6-2', task: '드레스 색상 및 스타일 확인', completed: true },
            { id: '6-3', task: '액세서리 준비 확인', completed: false }
          ]
        },
        {
          category: '헤어 장신구',
          tasks: [
            { id: '7-1', task: '티아라/화관 준비', completed: false },
            { id: '7-2', task: '헤어핀/밴드 준비', completed: false },
            { id: '7-3', task: '베일 준비', completed: false }
          ]
        },
        {
          category: '메이크업 준비',
          tasks: [
            { id: '8-1', task: '피부톤 맞춤 파운데이션 준비', completed: true },
            { id: '8-2', task: '립 컬러 준비', completed: true },
            { id: '8-3', task: '속눈썹 준비', completed: false }
          ]
        },
        {
          category: '고객 확인',
          tasks: [
            { id: '9-1', task: '고객 피부 상태 확인', completed: false },
            { id: '9-2', task: '알레르기 재확인', completed: true },
            { id: '9-3', task: '선호 스타일 최종 확인', completed: false }
          ]
        },
        {
          category: '일정 확인',
          tasks: [
            { id: '10-1', task: '촬영 일정 확인', completed: true },
            { id: '10-2', task: '웨딩홀 도착 시간 확인', completed: false },
            { id: '10-3', task: '혼주 메이크업 시간 확인', completed: false }
          ]
        }
      ]
    }
  ]);

  const [selectedChecklist, setSelectedChecklist] = useState<string | null>('1');

  const toggleTask = (checklistId: string, taskId: string) => {
    setChecklists(checklists.map(checklist => {
      if (checklist.id === checklistId) {
        const updatedItems = checklist.items.map(category => ({
          ...category,
          tasks: category.tasks.map(task =>
            task.id === taskId ? { ...task, completed: !task.completed } : task
          )
        }));
        
        const totalTasks = updatedItems.reduce((sum, cat) => sum + cat.tasks.length, 0);
        const completedTasks = updatedItems.reduce(
          (sum, cat) => sum + cat.tasks.filter(t => t.completed).length,
          0
        );
        const progress = Math.round((completedTasks / totalTasks) * 100);

        return { ...checklist, items: updatedItems, progress };
      }
      return checklist;
    }));
  };

  const selectedChecklistData = checklists.find(c => c.id === selectedChecklist);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">사전 준비 체크리스트</h1>
          <p className="text-sm text-gray-600 mt-1">메이크업 전 준비사항을 체크하세요</p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* 체크리스트 목록 */}
          <div className="col-span-4">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">고객 목록</h3>
              <div className="space-y-3">
                {checklists.map((checklist) => (
                  <div
                    key={checklist.id}
                    onClick={() => setSelectedChecklist(checklist.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedChecklist === checklist.id
                        ? 'border-pink-500 bg-pink-50'
                        : 'border-gray-200 hover:border-pink-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{checklist.customerName}</h4>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        checklist.progress === 100 ? 'bg-green-100 text-green-700' :
                        checklist.progress >= 50 ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {checklist.progress}%
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      예식일: {checklist.weddingDate}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          checklist.progress === 100 ? 'bg-green-500' :
                          checklist.progress >= 50 ? 'bg-blue-500' :
                          'bg-yellow-500'
                        }`}
                        style={{ width: `${checklist.progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 체크리스트 상세 */}
          <div className="col-span-8">
            {selectedChecklistData ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedChecklistData.customerName}</h2>
                    <p className="text-gray-600 mt-1">예식일: {selectedChecklistData.weddingDate}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">진행률</div>
                    <div className="text-3xl font-bold text-pink-600">{selectedChecklistData.progress}%</div>
                  </div>
                </div>

                {/* 알림 메시지 */}
                <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-alarm-line text-pink-600 text-xl w-5 h-5 flex items-center justify-center"></i>
                    <div>
                      <h4 className="font-semibold text-pink-900 mb-1">촬영 전날 자동 알림</h4>
                      <p className="text-sm text-pink-800">
                        예식 전날 자동으로 체크리스트 알림이 발송됩니다. 모든 항목을 완료했는지 확인하세요.
                      </p>
                    </div>
                  </div>
                </div>

                {/* 체크리스트 항목 */}
                <div className="space-y-6">
                  {selectedChecklistData.items.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-pink-600 w-5 h-5 flex items-center justify-center"></i>
                        {category.category}
                      </h3>
                      <div className="space-y-2">
                        {category.tasks.map((task) => (
                          <label
                            key={task.id}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={task.completed}
                              onChange={() => toggleTask(selectedChecklistData.id, task.id)}
                              className="w-5 h-5 text-pink-600 rounded cursor-pointer"
                            />
                            <span className={`flex-1 ${task.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                              {task.task}
                            </span>
                            {task.completed && (
                              <i className="ri-check-line text-green-600 w-5 h-5 flex items-center justify-center"></i>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* 완료 상태 */}
                {selectedChecklistData.progress === 100 && (
                  <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <i className="ri-checkbox-circle-fill text-green-600 text-2xl w-6 h-6 flex items-center justify-center"></i>
                      <div>
                        <h4 className="font-semibold text-green-900">모든 준비가 완료되었습니다!</h4>
                        <p className="text-sm text-green-800">예식 당일 완벽한 메이크업을 위한 준비가 끝났습니다.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <i className="ri-checkbox-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">고객을 선택하세요</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
